import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Anthropometric v2 API client (api-anthropometric-v2-service).
 *
 * Grouped model: one document per patient, `parameters[]` holds ≤ 7 entries
 * (one per distinct parameter) each with a cached `current` and an
 * append-only `statusHistory[]`.
 *
 * Contract notes:
 *
 * 1. `save` handles both the first entry and every subsequent correction —
 *    the server derives the audit event ("add" vs "correction") based on
 *    whether the parameter already has a `current` value. No separate
 *    update / delete endpoints exist: corrections are new immutable history
 *    entries.
 *
 * 2. Every mutating call needs `expectedVersion` (the last version the
 *    caller saw). On mismatch the server returns 409 with a fresh doc.
 *
 * 3. BMI + WHR are auto-written by the server on Weight/Height and
 *    Waist/Hip saves — never POST them directly (400).
 *
 * 4. The server fetches the patient's DOB + gender on every mutating call.
 *    If the monolith is unreachable the server returns 502 and the save
 *    fails — surface a "try again" message.
 *
 * 5. Body shape for save:
 *      { parameter, numericValue?, unit, valueDisplay, testDate,
 *        reason?, expectedVersion }
 *    where `numericValue` is metric-normalised (cm, kg) and `valueDisplay`
 *    is what the user typed (e.g. "5'11\"", "72.5", "O+"). `reason` is
 *    optional and only meaningful for corrections ("typo", free text).
 */
class AnthropometricService {
  constructor() {
    this.apiService = new APIService();
  }

  list(patientId, onResponse, onError) {
    const url = APIConfig.anthropometricV2.list.format(patientId);
    return this.apiService.get(url, onResponse, onError);
  }

  // Landing-page summary view — same response shape, but StatusHistory is
  // stripped per parameter (server-side). Cheaper payload for the 5-tile
  // overview strip that only reads Current.
  summary(patientId, onResponse, onError) {
    const url = APIConfig.anthropometricV2.summary.format(patientId);
    return this.apiService.get(url, onResponse, onError);
  }

  save(patientId, body, onResponse, onError) {
    const url = APIConfig.anthropometricV2.add.format(patientId);
    return this.apiService.post(url, body, onResponse, onError);
  }
}

export default AnthropometricService;
